import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import { fetchAssistantParameters, saveAssistantParameters, updateAssistantParameters } from "../../actions/dataConsoleAction";
import TextfieldComponent from "../../uiComponents/TextField/TextfieldComponent";
import DropdownComponent from "../../uiComponents/Dropdown/DropdownComponent";
import SliderComponent from "../../uiComponents/Slider/SliderComponent";
import RadioButton from "../../uiComponents/Radio/RadioButton";
import { Popup, Button, Grid, Icon, Image, Form } from "semantic-ui-react"; // Import for external link icon
import "../../uiComponents/Dropdown/DropdownComponent.css";
import "../../uiComponents/Slider/SliderComponent.css";
import "../../uiComponents/NumberInputBox/NumberInput.css";
import "./../../styles/AssistantSettings.css";
import "../../uiComponents/NumberInputBox/NumberInput.css";
import "../../uiComponents/TextField/TextFieldComponent.css";
import "../../uiComponents/Radio/RadioButton.css"; // Import RadioButton CSS
import Box from '@mui/material/Box';
import NumberInput from '../../uiComponents/NumberInputBox/NumberInput';
import TextField from '@mui/material/TextField';
import ToggleButtonGroup from "../../uiComponents/ToggleButtonGroup/ToggleButtonGroup"; // Import ToggleButtonGroup
import CustomToaster from "../../components/CustomToaster"; // Import CustomToaster
import PromptLoader from "../../components/PromptLoader"; // Import loader
import { width } from "@mui/system";
import { set } from "date-fns";
import modelsConfig from '../../config/models.json';

/**
 * AssistantSettings is a component that renders the Assistant Settings tab.
 * It includes parameters for customizing the assistant's behavior.
 */
const MAX_TOP_P = 1.0;
const MIN_TOP_P = 0.0; // UI minimum value is 0, but 0 is not allowed

const MAX_TEMP = 2.0;
const MIN_TEMP = 0.0; // UI minimum value is 0, but 0 is not allowed

const MAX_CHUNK_SIZE = 2000;
const MIN_CHUNK_SIZE = 0;
const MIN_CHUNK_OVERLAP = 0;

const MAX_KVALUE = 16;
const MIN_KVALUE = 1;

const AssistantSettings = ({ datasetId: propDatasetId, isReadOnly, onAssistantParamsChange, onServiceAgentToggle}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  let datasetId = propDatasetId;
  const { parameters, loading, error, status } = useSelector((state) => state.assistantParameters);

  const [assistantInstructions, setAssistantInstructions] = useState(process.env.REACT_APP_DEFAULT_PROMPT || "");
  const [openAIModel, setOpenAIModel] = useState(process.env.REACT_APP_DEFAULT_MODEL_NAME || "gpt-4o-mini");
  const [temperature, setTemperature] = useState(process.env.REACT_APP_DEFAULT_TEMPERATURE || 0.1);
  const [topPValue, setTopPValue] = useState(process.env.REACT_APP_DEFAULT_TOP_P_VALUE || 0.5);
  const [openAIProjectId, setOpenAIProjectId] = useState(""); // State for OpenAI Project ID
  const [chunkingStrategy, setChunkingStrategy] = useState("auto"); // State for Chinking Strategy
  const [serviceAgentEnabled, setServiceAgentEnabled] = useState(false); // State for Service Agent Persona
  const [codeInterpreterEnabled, setCodeInterpreterEnabled] = useState(false); // State for Code Interpreter
  const [showImagesOption, setShowImagesOption] = useState("fullPages");
  const [showSource, setShowSource] = useState(process.env.REACT_APP_DEFAULT_SHOW_SOURCE !== 'false');
  const [chunk, setChunk] = useState(
    parseInt(process.env.REACT_APP_CHUNK, 10) || 800
  );
  const [chunkOverlap, setChunkOverlap] = useState(
    parseInt(process.env.REACT_APP_CHUNK_OVERLAP, 10) || 320
  );
  const [datasetIds, setDatasetIds] = useState(null);
  const [toasterVisible, setToasterVisible] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");
  const [toasterAppearance, setToasterAppearance] = useState("success");
  const isMounted = useRef(true);

  const [isLoading, setIsLoading] = useState(false); // Local loading state
  // const [projectName, setProjectName] = useState(""); // State for Project Name
  const [kValue, setKValue] = useState(8); // Default value for kValue
  const [isButtonEnabled, setIsButtonEnabled] = useState(false); // State to track button enable/disable
  const [isReadOnlyState, setIsReadOnlyState] = useState(false); // Define isReadOnlyState
  useEffect(() => {
    if (!propDatasetId) {
      const urlParams = new URLSearchParams(location.search);
      datasetId = urlParams.get("c");
    }
  }, [propDatasetId, location]);

  const fetchParameters = async () => {
    if (datasetId) {
      try {
        await dispatch(fetchAssistantParameters(datasetId));
      } catch (error) {
        console.error("Error fetching assistant parameters:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchParameters();
  }, [datasetId]);

  // useEffect(() => {
  //   if (status === 200) {
  //     // Avoid redundant fetch if parameters are already fetched
  //     if (!parameters) {
  //       fetchParameters();
  //     }
  //   } else if (status >= 300 || status === undefined || status === null || status === 400) {
  //     // console.error("Error fetching assistant parameters. Status:", status);
  //     handleReset();
  //     setIsLoading(false);
  //   }
  // }, [status, parameters]);

  useEffect(() => {
    if (parameters) {
      setOpenAIModel(parameters.openAIModel || "gpt-4o-mini");
      setTemperature(parameters.temperature || 0.1);
      setTopPValue(parameters.topP || 0.5);
      setChunkingStrategy(parameters.chunkingStrategy || "auto");
      setChunk(parameters.chunkSize || 800);
      setChunkOverlap(parameters.chunkOverlap || 320);
      setAssistantInstructions(parameters.systemInstructions || process.env.REACT_APP_DEFAULT_PROMPT);
      setServiceAgentEnabled(parameters.serviceAgentEnabled || false);
      setOpenAIProjectId(parameters.assistantId || ""); // Set OpenAI Project ID
      // setProjectName(parameters.projectId || ""); // Set Project Name
      setKValue(parameters.kValue || 8); // Set K Value
      setCodeInterpreterEnabled(parameters.codeInterpreterEnabled || false);
      setShowImagesOption(parameters.showImagesOption || 'fullPages');
      setShowSource(parameters.showSource !== false);
    }
    return () => {
      isMounted.current = false; // Cleanup on unmount
    };
  }, [parameters]);

  // Notify parent component of parameter changes
  useEffect(() => {
    if (onAssistantParamsChange) {
      onAssistantParamsChange({
        assistantInstructions,
        openAIModel,
        temperature,
        topPValue,
        openAIProjectId,
        chunkingStrategy,
        serviceAgentEnabled,
        codeInterpreterEnabled,
        chunk,
        chunkOverlap,
        // projectName,
        kValue,
        showImagesOption,
        showSource,
      });
    }
  }, [
    assistantInstructions,
    openAIModel,
    temperature,
    topPValue,
    openAIProjectId,
    chunkingStrategy,
    serviceAgentEnabled,
    codeInterpreterEnabled,
    chunk,
    chunkOverlap,
    // projectName,
    kValue,
    showImagesOption,
    showSource,
    onAssistantParamsChange,
  ]);

  useEffect(() => {
    // Compare current values with initial parameters
    const hasChanges =
      assistantInstructions !== (parameters?.systemInstructions || process.env.REACT_APP_DEFAULT_PROMPT) ||
      openAIModel !== (parameters?.openAIModel || "gpt-4o-mini") ||
      temperature !== (parameters?.temperature || 0.1) ||
      topPValue !== (parameters?.topP || 0.5) ||
      chunkingStrategy !== (parameters?.chunkingStrategy || "auto") ||
      serviceAgentEnabled !== (parameters?.serviceAgentEnabled || false) ||
      codeInterpreterEnabled !== (parameters?.codeInterpreterEnabled || false) ||
      chunk !== (parameters?.chunkSize || 800) ||
      chunkOverlap !== (parameters?.chunkOverlap || 320) ||
      kValue !== (parameters?.kValue || 8) ||
      showImagesOption !== (parameters?.showImagesOption || process.env.REACT_APP_DEFAULT_SHOW_IMAGES_OPTION || 'fullPages') || // Added fallback
      showSource !== (parameters?.showSource !== false);

    setIsButtonEnabled(hasChanges); // Enable buttons if any value has changed
  }, [
    assistantInstructions,
    openAIModel,
    temperature,
    topPValue,
    chunkingStrategy,
    serviceAgentEnabled,
    codeInterpreterEnabled,
    chunk,
    chunkOverlap,
    kValue,
    parameters,
    showImagesOption,
    showSource,
  ]);

  // Utility function to get the appropriate class name based on the URL
  const getClassName = (baseClass, location) => {
    if (location.pathname.includes('aiassistant')) {
      return ` aiassistant-${baseClass}`;
    } else if (location.pathname.includes('dataset')) {
      return `dataset-${baseClass}`;
    }
    return baseClass;
  };

  const showToaster = (message, appearance) => {
    setToasterVisible(true);
    setToasterMessage(message);
    setToasterAppearance(appearance);
  };

  const closeToaster = () => {
    setToasterVisible(false);
  };

  const handleServiceAgentToggle = () => {
    const newValue = !serviceAgentEnabled;
    setServiceAgentEnabled(newValue);

    // Notify Dataconsole.js about the updated value
    if (onServiceAgentToggle) {
      onServiceAgentToggle(newValue);
    }
  };

  const handleCodeInterpreterToggle = () => {
    setCodeInterpreterEnabled((prevState) => !prevState);
  };

  const handleChunkChange = (event, newValue) => {
    setChunk(newValue);
    const newMaxChunkOverlap = Math.floor(newValue * 0.4);
    setChunkOverlap((prevOverlap) => Math.min(prevOverlap, newMaxChunkOverlap));
  };

  const handleChunkOverlapChange = (event, newValue) => {
    setChunkOverlap(newValue);
  };

  const handleKValueChange = (event, newValue) => {
    setKValue(newValue);
  };

  const handleShowImagesOption = (e, { value }) => {
    setShowImagesOption(value);
  };

  const handleShowSourceChange = (e, { value }) => {
    setShowSource(value === 'yes');
  };

  // Model options loaded from config/models.json
  const modelOptions = modelsConfig.chatModels;

  const handleReset = () => {
    setAssistantInstructions(process.env.REACT_APP_DEFAULT_PROMPT || "");
    setOpenAIModel("gpt-5.2");
    setTemperature(0.1);
    setTopPValue(0.5);
    setOpenAIProjectId(""); // Reset OpenAI Project ID
    setChunkingStrategy("auto"); // Reset Chunking Strategy
    setServiceAgentEnabled(false); // Reset Service Agent Persona
    setCodeInterpreterEnabled(false); // Reset Code Interpreter
    setChunk(800); // Reset chunk size
    setChunkOverlap(320); // Reset chunk overlap
    // setProjectName(""); // Reset Project Name
    setKValue(8); // Reset K Value
    setShowImagesOption(process.env.REACT_APP_DEFAULT_SHOW_IMAGES_OPTION || 'fullPages'); // Reset Show Images Option
    setShowSource(process.env.REACT_APP_DEFAULT_SHOW_SOURCE !== 'false'); // Reset Show Source
  };

  const handleSave = async () => {
    setIsLoading(true); // Start loader when saving
    try {
      const payload = {
        datasetId,
        openAIModel,
        temperature,
        topP: topPValue,
        serviceAgentEnabled,
        codeInterpreterEnabled,
        chunkingStrategy,
        chunkSize: chunk,
        chunkOverlap,
        systemInstructions: assistantInstructions,
        openAIProjectId,
        // projectName, 
        kValue,
        showImagesOption,
        showSource,
      };

      await dispatch(saveAssistantParameters(datasetId, payload));
      showToaster("Assistant settings saved successfully", "success");

      // Fetch the updated assistant parameters after saving
      await fetchParameters();
    } catch (error) {
      console.error("Error saving assistant settings:", error);
      showToaster("Failed to save assistant settings. Please try again.", "error");
    } finally {
      setIsLoading(false); // Stop loader
    }
  };

  const assistantLink = `https://platform.openai.com/assistants/${openAIProjectId}`;

  const isAiAssistant = getClassName("", location).includes("aiassistant");

  return (
    <div className={getClassName("assistant-settings-container", location)}>
      {toasterVisible && (
        <CustomToaster
          message={toasterMessage}
          duration={3000}
          onClose={closeToaster}
          position="right-top"
          appearance={toasterAppearance}
        />
      )}

      {isLoading && (
        <div style={{ position: "relative", height: "100vh" }} className="prompt-loader">
          <PromptLoader loaderType="default" />
        </div>
      )}
      {!isLoading && (
        <Grid stackable padded="vertically" relaxed="very">
          {/* Assistant ID and Project Name */}
          {!isAiAssistant && (
              <Grid.Row>
                <Grid.Column width={8} className={getClassName('openai-box', location)}>
                  <div style={{ minHeight: "100px" }}>
                    <Popup
                      content="Provide the OpenAI Project ID."
                      inverted
                      position="top left"
                      style={{ borderRadius: "15px" }}
                      trigger={<label className="input-label">Assistant ID</label>}
                    />
                    <TextfieldComponent
                      value={openAIProjectId}
                      onChange={setOpenAIProjectId}
                      className={getClassName('ai-textbox-input', location)}
                      style={{ borderRadius: "15px" }}
                      data-cy="openai-project-id-input"
                      variant="outlined"
                      placeholder="Enter your OpenAI Project ID"
                      error={!openAIProjectId.trim()}
                      helperText={!openAIProjectId.trim() ? "This field is required." : ""}
                      disabled={true}
                    />
                  </div>
                </Grid.Column>

                <Grid.Column width={8} className={getClassName('assistant-link-box', location)} >
                  <div style={{ minHeight: "100px" }}>
                    <Popup
                      content="Go to the Assistant page for this project."
                      inverted
                      position="top left"
                      style={{ borderRadius: "340px" }}
                      trigger={<label className="input-label">Assistant Link</label>}
                    />
                    <Button
                      as="a"
                      href={assistantLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={getClassName('ai-textbox-input', location)}
                      data-cy="assistant-link-button"
                      disabled={!openAIProjectId.trim()} // Disable if no Project ID
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#0053ed",
                        color: "#fff",
                        borderRadius: "15px",
                        padding: "10px 20px",
                        fontWeight: "bold",
                        textDecoration: "none",
                        width: "90%",
                      }}
                    >
                      Go to Assistant
                      <Image src="/images/view-more.svg" alt="Empty State" 
                        style={{ width: "20px", height: "20px", marginLeft: "50px" }}
                      />
                    </Button>
                  </div>
                </Grid.Column>
              </Grid.Row>
          )}

          {/* Assistant Instructions */}
          <Grid.Row>
            <Grid.Column width={16}>
              <Popup
                content="The prompt used for generating Q&A responses."
                inverted
                position="top left"
                style={{ borderRadius: "15px" }}
                trigger={<label className="textarea-label">Assistant Instructions</label>}
              />
              <TextfieldComponent
                value={assistantInstructions}
                onChange={setAssistantInstructions}
                className={getClassName('ai-textfield-input', location)}
                rows={7}
                style={{ border: "1px" }}
                dataCy="assistant-instructions-textarea"
              />
            </Grid.Column>
          </Grid.Row>

          {/* Temperature and Top P */}
          <Grid.Row>
            <Grid.Column width={8} className={getClassName('temp-box', location)}>
              <Popup
                content="% allowed for the model to consider outside world information."
                inverted
                position="top left"
                style={{ borderRadius: "15px" }}
                trigger={<label className="slider-labels">Temperature</label>}
              />
              <Box className={getClassName('slider', location)}>
                <SliderComponent
                  value={temperature}
                  min={MIN_TEMP}
                  max={MAX_TEMP}
                  step={0.01}
                  onChange={(e, newValue) => {
                    if (newValue >= 0.01) setTemperature(newValue); // Prevent setting 0
                  }}
                  className="assistant-temperature-slider"
                  dataCy="assistant-temperature-slider"
                />
                <NumberInput
                  value={temperature}
                  min={MIN_TEMP}
                  max={MAX_TEMP}
                  step={0.01}
                  onChange={(value) => {
                    if (value >= 0.01) setTemperature(value); // Prevent setting 0
                  }}
                  className={getClassName('output-box', location)}
                  dataCy="temperature-value-output-box"
                />
              </Box>
            </Grid.Column>

            <Grid.Column width={8} className={getClassName('top-box', location)}>
              <Popup
                content="Instruct whether response should be simple or complex words."
                inverted
                position="top left"
                style={{ borderRadius: "15px" }}
                trigger={<label className="slider-labels">Top P</label>}
              />
              <Box className={getClassName('slider', location)}>
                <SliderComponent
                  value={topPValue}
                  min={MIN_TOP_P}
                  max={MAX_TOP_P}
                  step={0.01}
                  onChange={(e, newValue) => {
                    if (newValue >= 0.01) setTopPValue(newValue); // Prevent setting 0
                  }}
                  marks={[{ value: MIN_TOP_P }, { value: MAX_TOP_P }]}
                  className="assistant-top-p-slider"
                  dataCy="assistant-top-p-slider"
                />
                <NumberInput
                  value={topPValue}
                  min={MIN_TOP_P}
                  max={MAX_TOP_P}
                  step={0.01}
                  onChange={(value) => {
                    if (value >= 0.01) setTopPValue(value); // Prevent setting 0
                  }}
                  className={getClassName('output-box', location)}
                  dataCy="top-p-value-output-box"
                />
              </Box>
            </Grid.Column>
          </Grid.Row>

          {/* Model Name and K Value */}
          <Grid.Row style={{ marginTop: "-1em" }}>
            <Grid.Column width={8} className={getClassName('model-box', location)}>
              <div style={{ minHeight: "0px" }}>
                <Popup
                  content="The name of the OpenAI model being used."
                  inverted
                  position="top left"
                  style={{ borderRadius: "15px" }}
                  trigger={<label className="dropdown-labels" style={{ marginBottom: "1.5em" }}>Model Name</label>}
                />
                <DropdownComponent
                  selection
                  value={openAIModel}
                  onChange={(e, { value }) => setOpenAIModel(value)}
                  options={modelOptions}
                  style={{ marginTop: "1em" }}
                  className={getClassName('dropdowns-input', location)}
                  data-cy="assistant-model-dropdown"
                />
              </div>
            </Grid.Column>

            <Grid.Column width={8} className={getClassName('top-box', location)}>
              <Popup
                content="Count of max source documents that can be used to arrive at a response."
                inverted
                position="top left"
                style={{ borderRadius: "15px" }}
                trigger={<label className="slider-labels">K Value</label>}
              />
              <Box className={getClassName('slider', location)}>
                <SliderComponent
                  value={kValue}
                  min={MIN_KVALUE}
                  max={MAX_KVALUE}
                  step={1}
                  onChange={handleKValueChange}
                  marks={[{ value: MIN_KVALUE }, { value: MAX_KVALUE }]}
                  className="assistant-kvalue-slider"
                  dataCy="kvalue-slider"
                />
                <NumberInput
                  value={kValue}
                  min={MIN_KVALUE}
                  max={MAX_KVALUE}
                  step={1}
                  onChange={(value) => setKValue(value)}
                  className={getClassName('output-box', location)}
                  dataCy="kvalue-output-box"
                />
              </Box>
            </Grid.Column>
          </Grid.Row>
          {/* Show full pages */}
          <Grid.Row>
                <Grid.Column width={16}>
                  <div style={{ minHeight: "120px" }}>
                    <Popup
                      content={
                        <div>
                          Select the preferred Image response format:
                          <ul>
                            <li>Full Pages (Entire Pages)</li>
                            <li>Don't Show images (No Images)</li>
                            <li>Thumbnails (Small Previews of Full Pages)</li>
                          </ul>
                        </div>
                    }
                      inverted
                      position="top left"
                      style={{ borderRadius: "15px" }}
                      trigger={<label className="radio-label" style={{ marginBottom: "1.5em" }}>Show Images in response as</label>}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <RadioButton
                      value="fullPages"
                      checked={showImagesOption === 'fullPages'}
                      onChange={handleShowImagesOption}
                      className={getClassName('radio-input', location)}
                      label="Full Pages"
                      style={{ marginBottom: '10px' }}
                      dataCy="radio-fullPages"
                      disabled={isReadOnlyState}
                    />
                    <RadioButton
                      value="dontShowImages"
                      checked={showImagesOption === 'dontShowImages'}
                      onChange={handleShowImagesOption}
                      className={getClassName('radio-input', location)}
                      label="Don't Show images"
                      style={{ marginBottom: '10px' }}
                      dataCy="radio-dontShowImages"
                      disabled={isReadOnlyState}
                    />
                    <RadioButton
                      value="thumbnails(fullPages)"
                      checked={showImagesOption === 'thumbnails(fullPages)'}
                      onChange={handleShowImagesOption}
                      className={getClassName('radio-input', location)}
                      label="Thumbnails (full pages)"
                      style={{ marginBottom: '10px' }}
                      dataCy="radio-thumbnails"
                      disabled={isReadOnlyState}
                    />
                      </div>
                  </div>
                </Grid.Column>
              </Grid.Row>

              {/* Show Source */}
              <Grid.Row>
                <Grid.Column width={16}>
                  <div style={{ minHeight: "60px" }}>
                    <Popup
                      content="Show or hide the source documents in the response."
                      inverted
                      position="top left"
                      style={{ borderRadius: "15px" }}
                      trigger={<label className="radio-label" style={{ marginBottom: "1.5em" }}>Show Source</label>}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <RadioButton
                        value="yes"
                        checked={showSource === true}
                        onChange={handleShowSourceChange}
                        className={getClassName('radio-input', location)}
                        label="Yes"
                        style={{ marginBottom: '10px' }}
                        dataCy="radio-showSource-yes"
                        disabled={isReadOnlyState}
                      />
                      <RadioButton
                        value="no"
                        checked={showSource === false}
                        onChange={handleShowSourceChange}
                        className={getClassName('radio-input', location)}
                        label="No"
                        style={{ marginBottom: '10px' }}
                        dataCy="radio-showSource-no"
                        disabled={isReadOnlyState}
                      />
                    </div>
                  </div>
                </Grid.Column>
              </Grid.Row>

          {/* Service Agent Persona */}
          {!isAiAssistant && (
            <Grid.Row style={{ marginTop: "-1em", marginBottom: "0.5em" }}>
              <Grid.Column width={16}>
                <div style={{ minHeight: "30px" }}>
                  <Popup
                    content="Enable or disable the Service Agent Persona."
                    inverted
                    position="top left"
                    style={{ borderRadius: "15px" }}
                    trigger={<label className="toggle-label">Service Agent Persona</label>}
                  />
                  <ToggleButtonGroup
                    isSimpleToggle
                    isEnabled={serviceAgentEnabled}
                    onToggle={handleServiceAgentToggle}
                    className="service-agent-toggle"
                    dataCy="service-agent-persona-toggle"
                    style={{ marginLeft: "14em" }}
                  />
                </div>
              </Grid.Column>
            </Grid.Row>
          )}

          {/* Code Interpreter */}
          <Grid.Row className={getClassName('code-box', location)} style={{ marginTop: "0em", marginBottom: "0.5em" }}>
            <Grid.Column width={16}>
              <Popup
                content="Enable or disable the Code Interpreter."
                inverted
                position="top left"
                style={{ borderRadius: "15px" }}
                trigger={<label className="toggle-label">Code Interpreter</label>}
              />
              <ToggleButtonGroup
                isSimpleToggle
                isEnabled={codeInterpreterEnabled}
                onToggle={handleCodeInterpreterToggle}
                className={getClassName('code-agent-toggle', location)}
                dataCy="code-interpreter-toggle"
                style={{ marginLeft: "14em" }}
              />
            </Grid.Column>
          </Grid.Row>

          {/* Chunking Strategy */}
          {!isAiAssistant && (
            <>
              <Grid.Row>
                <Grid.Column width={16}>
                  <Popup
                    content="Select the chunking strategy for processing."
                    inverted
                    position="top left"
                    style={{ borderRadius: "15px" }}
                    trigger={<label className="radio-label" style={{ marginBottom: "1.5em" }}>Chunking Strategy</label>}
                  />
                  <div className="radio-group">
                    <RadioButton
                      label="Dynamic"
                      value="dynamic"
                      checked={chunkingStrategy === "dynamic"}
                      onChange={(e, { value }) => setChunkingStrategy(value)}
                      className="aiassistant-radio-input"
                    />
                    <RadioButton
                      label="Auto"
                      value="auto"
                      checked={chunkingStrategy === "auto"}
                      onChange={(e, { value }) => setChunkingStrategy(value)}
                      className="aiassistant-radio-input"
                    />
                  </div>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row style={{ marginTop: "1em" }}>
                <Grid.Column>
                  <Popup
                    content="The characters size of each chunk in which the file will be divided."
                    inverted
                    position="top left"
                    style={{ borderRadius: "15px" }}
                    trigger={<label className="input-label">Characters Size Per Chunk</label>}
                  />
                  <SliderComponent
                    min={MIN_CHUNK_SIZE}
                    max={MAX_CHUNK_SIZE}
                    value={chunk}
                    onChange={handleChunkChange}
                    disabled={chunkingStrategy === "auto"}
                    step={100}
                    marks={[{ value: MIN_CHUNK_SIZE }, { value: MAX_CHUNK_SIZE }]}
                    className={`${getClassName("slider-input", location)} half-width-slider`}
                    dataCy="chunk-size-slider"
                  />
                  <NumberInput
                    value={chunk}
                    min={MIN_CHUNK_SIZE}
                    max={MAX_CHUNK_SIZE}
                    step={100}
                    onChange={(value) => setChunk(value)}
                    className={getClassName('output-box', location)}
                    dataCy="chunk-size-output-box"
                    disabled={chunkingStrategy === "auto" }
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <Popup
                    content="The overlap characters size between chunks in which the file will be divided. Note: Ideally chunk overlap should be 10-20% of the chunk size."
                    inverted
                    position="top left"
                    style={{ borderRadius: "15px" }}
                    trigger={<label className="input-label">Overlapping Characters Between Chunks</label>}
                  />
                  <SliderComponent
                    min={MIN_CHUNK_OVERLAP}
                    max={Math.floor(chunk * 0.4)}
                    value={chunkOverlap}
                    onChange={handleChunkOverlapChange}
                    disabled={chunkingStrategy === "auto"}
                    step={1}
                    marks={[{ value: MIN_CHUNK_OVERLAP }, { value: Math.floor(chunk * 0.4) }]}
                    className={`${getClassName("slider-input", location)} half-width-slider`}
                    dataCy="chunk-overlap-slider"
                  />
                  <NumberInput
                    value={chunkOverlap}
                    min={MIN_CHUNK_OVERLAP}
                    max={Math.floor(chunk * 0.4)}
                    step={1}
                    onChange={(value) => setChunkOverlap(Math.min(parseInt(value), Math.floor(chunk * 0.4)))}
                    className={getClassName('output-box', location)}
                    dataCy="chunk-overlap-output-box"
                    disabled={chunkingStrategy === "auto"}
                  />
                </Grid.Column>
              </Grid.Row>
            </>
          )}

          {/* Buttons */}
          <Grid.Row>
            <Grid.Column width={16}>
              <Button
                onClick={handleReset}
                className="clear-button"
                data-cy="reset-button-cy"
              >
                Reset
              </Button>
              <Button
                onClick={handleSave}
                className="saved-buttons"
                data-cy="save-button-cy"
                disabled={!isButtonEnabled} // Disable if no changes or read-only
              >
                Save
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      )}
    </div>
  );
};

export default AssistantSettings;